<template>
  <transition name="fade">
    <div
      v-if="visible && targetElement"
      class="onboarding-tooltip"
      :class="positionClass"
      :style="tooltipStyle"
      ref="tooltip"
    >
      <div class="tooltip-content">
        <h4 v-if="title" class="tooltip-title">{{ title }}</h4>
        <p class="tooltip-message">{{ message }}</p>
      </div>
      
      <div class="tooltip-controls">
        <b-button
          v-if="showPrevious"
          class="apply_btn apply_btn_prev"
          variant="outline-primary"
          size="sm"
          @click="$emit('previous')"
        >
          Previous
        </b-button>
        
        <b-button
          class="apply_btn apply_btn_next"
          variant="primary"
          size="sm"
          @click="$emit('next')"
        >
          {{ isLastStep ? 'Finish' : 'Next' }}
        </b-button>
        
        <b-button
          v-if="skippable"
          class="apply_btn_skip"
          variant="link"
          size="sm"
          @click="$emit('skip')"
        >
          {{ isLastStep ? '' : 'Skip tutorial' }}
        </b-button>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: "OnboardingTooltip",
  props: {
    visible: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: "",
    },
    message: {
      type: String,
      required: true,
    },
    position: {
      type: String,
      default: "bottom",
      validator: (value) => ["top", "bottom", "left", "right"].includes(value),
    },
    targetElement: {
      type: HTMLElement,
      required: true,
    },
    showPrevious: {
      type: Boolean,
      default: true,
    },
    isLastStep: {
      type: Boolean,
      default: false,
    },
    skippable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      tooltipWidth: 320,
      tooltipHeight: 180,
      repositionKey: 0,
    };
  },
  computed: {
    positionClass() {
      return `tooltip-${this.actualPosition}`;
    },
    
    actualPosition() {
      if (!this.targetElement) return this.position;
      
      const rect = this.targetElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const spacing = 15;
      
      // Calculate available space in each direction
      const spaceTop = rect.top;
      const spaceBottom = viewportHeight - rect.bottom;
      const spaceLeft = rect.left;
      const spaceRight = viewportWidth - rect.right;
      
      let finalPosition = this.position;
      
      // Check if requested position has enough space, if not, find best alternative
      if (this.position === 'top' && spaceTop < this.tooltipHeight + spacing + 20) {
        finalPosition = spaceBottom > spaceTop ? 'bottom' : 'right';
      } else if (this.position === 'bottom' && spaceBottom < this.tooltipHeight + spacing + 20) {
        finalPosition = spaceTop > spaceBottom ? 'top' : 'right';
      } else if (this.position === 'left' && spaceLeft < this.tooltipWidth + spacing + 20) {
        finalPosition = spaceRight > spaceLeft ? 'right' : 'bottom';
      } else if (this.position === 'right' && spaceRight < this.tooltipWidth + spacing + 20) {
        finalPosition = spaceLeft > spaceRight ? 'left' : 'bottom';
      }
      
      return finalPosition;
    },
    
    tooltipStyle() {
      if (!this.targetElement) return { display: 'none' };

      // Force recalculation
      this.repositionKey;

      const rect = this.targetElement.getBoundingClientRect();
      const spacing = 20;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const safeMargin = 20; // Minimum distance from screen edges

      let left, top, transform;
      const position = this.actualPosition;

      // Calculate base position
      switch (position) {
        case "top":
          left = rect.left + rect.width / 2;
          top = rect.top - spacing;
          transform = "translate(-50%, -100%)";
          
          // Ensure tooltip doesn't go above screen
          if (top - this.tooltipHeight < safeMargin) {
            top = rect.bottom + spacing;
            transform = "translate(-50%, 0)";
          }
          break;

        case "bottom":
          left = rect.left + rect.width / 2;
          top = rect.bottom + spacing;
          transform = "translate(-50%, 0)";
          
          // Ensure tooltip doesn't go below screen
          if (top + this.tooltipHeight > viewportHeight - safeMargin) {
            top = rect.top - spacing;
            transform = "translate(-50%, -100%)";
          }
          break;

        case "left":
          left = rect.left - spacing;
          top = rect.top + rect.height / 2;
          transform = "translate(-100%, -50%)";
          
          // Ensure tooltip doesn't go off left edge
          if (left - this.tooltipWidth < safeMargin) {
            left = rect.right + spacing;
            transform = "translate(0, -50%)";
          }
          break;

        case "right":
          left = rect.right + spacing;
          top = rect.top + rect.height / 2;
          transform = "translate(0, -50%)";
          
          // Ensure tooltip doesn't go off right edge
          if (left + this.tooltipWidth > viewportWidth - safeMargin) {
            left = rect.left - spacing;
            transform = "translate(-100%, -50%)";
          }
          break;
      }

      // Final boundary checks for horizontal positioning (top/bottom positions)
      if (position === "top" || position === "bottom") {
        const halfWidth = this.tooltipWidth / 2;
        if (left - halfWidth < safeMargin) {
          left = halfWidth + safeMargin;
        } else if (left + halfWidth > viewportWidth - safeMargin) {
          left = viewportWidth - halfWidth - safeMargin;
        }
      }

      // Final boundary checks for vertical positioning (left/right positions)
      if (position === "left" || position === "right") {
        const halfHeight = this.tooltipHeight / 2;
        if (top - halfHeight < safeMargin) {
          top = halfHeight + safeMargin;
        } else if (top + halfHeight > viewportHeight - safeMargin) {
          top = viewportHeight - halfHeight - safeMargin;
        }
      }

      // Absolute minimum constraints
      top = Math.max(safeMargin, Math.min(top, viewportHeight - safeMargin));
      left = Math.max(safeMargin, Math.min(left, viewportWidth - safeMargin));

      return {
        left: `${left}px`,
        top: `${top}px`,
        transform,
        maxWidth: `${Math.min(350, viewportWidth - 40)}px`,
      };
    },
  },
  
  mounted() {
    this.updateDimensions();
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('scroll', this.handleScroll, true);
  },
  
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scroll', this.handleScroll, true);
  },
  
  methods: {
    updateDimensions() {
      this.$nextTick(() => {
        if (this.$refs.tooltip) {
          const rect = this.$refs.tooltip.getBoundingClientRect();
          this.tooltipWidth = rect.width || 320;
          this.tooltipHeight = rect.height || 180;
          this.repositionKey++;
        }
      });
    },
    
    handleResize() {
      this.updateDimensions();
    },
    
    handleScroll() {
      this.repositionKey++;
    }
  },
  
  watch: {
    targetElement() {
      this.updateDimensions();
    },
    visible(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.updateDimensions();
        });
      }
    }
  }
};
</script>

<style scoped>
.onboarding-tooltip {
  position: fixed;
  z-index: 10001;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  padding: 20px;
  min-width: 280px;
  max-width: 350px;
  width: auto;
  pointer-events: auto;
  display: inline-block;
  border: 2px solid #ff7222;
}

.tooltip-content {
  margin-bottom: 16px;
}

.tooltip-title {
  font-size: 1.2em;
  font-weight: 600;
  margin-bottom: 8px;
  color: #ff7222;
}

.tooltip-message {
  margin-bottom: 0;
  line-height: 1.5;
  color: #333;
  font-size: 0.95rem;
}

.tooltip-controls {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}

.apply_btn,
.apply_btn_prev,
.apply_btn_next,
.apply_btn_skip {
  flex-shrink: 0;
}

.apply_btn {
  background-color: #ff7222;
  color: white;
  text-align: center;
  min-width: 90px;
  height: 40px;
  border-radius: 14px;
  border: none;
  margin: 0;
  padding: 0 16px;
  font-size: 1rem;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s;
}

.apply_btn_prev {
  background-color: #eee;
  color: #333;
}

.apply_btn_prev:hover {
  background-color: #ddd;
}

.apply_btn_next {
  background-color: #ff7222;
  color: white;
}

.apply_btn_next:hover {
  background-color: rgb(255, 141, 49);
  transform: translateY(-1px);
}

.apply_btn_next:active {
  background-color: rgb(239, 92, 0);
  transform: translateY(0);
}

.apply_btn_skip {
  background: none;
  color: #888;
  border: none;
  text-decoration: underline;
  min-width: auto;
  height: 40px;
  margin: 0;
  padding: 0 12px;
  font-size: 0.9rem;
  cursor: pointer;
}

.apply_btn_skip:hover {
  color: #ff7222;
  background: none;
}

.tooltip-top::after,
.tooltip-bottom::after,
.tooltip-left::after,
.tooltip-right::after {
  content: "";
  position: absolute;
  border: 10px solid transparent;
}

.tooltip-top::after {
  border-top-color: #ff7222;
  bottom: -20px;
  left: 50%;
  transform: translateX(-50%);
}

.tooltip-bottom::after {
  border-bottom-color: #ff7222;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
}

.tooltip-left::after {
  border-left-color: #ff7222;
  right: -20px;
  top: 50%;
  transform: translateY(-50%);
}

.tooltip-right::after {
  border-right-color: #ff7222;
  left: -20px;
  top: 50%;
  transform: translateY(-50%);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>


// WEBPACK FOOTER //
// src/components/onboarding/OnboardingTooltip.vue